import { Form } from 'antd';
import { InventoryDocumentType, ProductVersion } from 'models';
import { useCallback, useEffect, useState } from 'react';
import useAuthContext from 'stores/auth-context';
import {
  useCreateInventoryCheckOrderMutation,
  useGenerateCodeMutation,
  useGetInventoryCheckOrderByIdQuery,
  useGetProductsQuery,
  useGetStatusQuery,
  useUpdateInventoryCheckOrderMutation,
} from 'services';
import { ProductData, QueryParams } from 'types';
import { useDebouncedCallback } from 'use-debounce';
import { generateRandomBarcode } from 'utils';
import { showAlertNotification } from 'components/common';
import { useNavigate } from 'react-router-dom';

export const useCreateCheckOrder = () => {
  const [form] = Form.useForm();
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get('id') || '';
  const [selectedProducts, setSelectedProducts] = useState<ProductVersion[]>([]);
  const { profile, loading } = useAuthContext();
  const [selectedProduct, setSelectedProduct] = useState<ProductData | undefined>(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [generateCode, { isLoading: isLoadingGenerateCode }] = useGenerateCodeMutation();
  const [code, setCode] = useState<string | undefined>(undefined);
  const [createCheckOrder, { isLoading: isLoadingCreateCheckOrder }] = useCreateInventoryCheckOrderMutation();
  const [updateCheckOrder, { isLoading: isLoadingUpdateCheckOrder }] = useUpdateInventoryCheckOrderMutation();
  const navigate = useNavigate();
  const { data: checkOrderData, isLoading: isLoadingCheckOrderData } = useGetInventoryCheckOrderByIdQuery(
    {
      id: orderId,
      query: {
        include: 'status|createdBy.baseUser|inventoryDocumentItems',
      },
    },
    { skip: !orderId },
  );
  const [filtered, setFiltered] = useState<QueryParams>({
    size: 20,
    page: 1,
    include: 'product.productVersions|category|status|brand|baseUnit|currencyUnit|productImages.image',
    filter: 'isActive:eq:true&&status.code:eq:pr-active',
    sort: 'updatedAt:desc',
  });
  const { data: statusData } = useGetStatusQuery({
    query: {
      filter: 'isActive:eq:true&&type:eq:' + InventoryDocumentType.INVENTORY_CHECK_ORDER,
    },
  });
  const { data: productVersionsData, isLoading: isLoadingProducts } = useGetProductsQuery({
    query: filtered,
  });
  const inprogressId = statusData?.data?.find((status) => status.code === 'invcheck-inprogress')?.id;
  const completedId = statusData?.data?.find((status) => status.code === 'invcheck-completed')?.id;
  const debouncedSearch = useDebouncedCallback((searchTerm: string) => {
    setFiltered(
      (prev) =>
        ({
          ...prev,
          size: searchTerm ? 'unlimited' : 20,
          page: 1,
          searchKey: 'name|color|dimension|sizeSpec|product.code',
          searchValue: searchTerm || '',
        }) as unknown as QueryParams,
    );
  }, 300); // Debounce 300ms
  useEffect(() => {
    if (!checkOrderData) {
      const codeGen = async () => {
        if (!orderId) {
          const response = await generateCode({ code: 'PK' });
          setCode(response.data);
        }
      };
      codeGen();
    } else {
      setCode(checkOrderData?.data?.code);
    }
  }, []);
  useEffect(() => {
    if (profile) {
      form.setFieldsValue({
        userName: profile?.data.baseUser?.name || '',
      });
    }
    form.setFieldsValue({
      code: checkOrderData?.data?.code || code,
    });
    if (checkOrderData) {
      setDescription(checkOrderData?.data?.description || '');
      setSelectedProducts(
        checkOrderData?.data?.inventoryDocumentItems?.map((item) => ({
          id: item.productVersionId,
          name: item.name,
          quantity: item.quantity,
          actualQuantity: item.actualQuantity,
          costPrice: item.costPrice,
          total: item.total,
          categoryId: item.category,
          reason: item.description,
          color: item.color,
          sizeSpec: item.sizeSpec,
          dimension: item.productDimension,
        })) || [],
      );
    }
  }, [profile, code, checkOrderData]);

  const handleSelectProductToList = (product: ProductVersion) => {
    if (selectedProducts.some((p) => p.id === product.id)) return;
    setSelectedProducts((prev) => [...prev, product]);
  };

  const handleRemoveProduct = (productId: string) => {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== productId));
  };

  const handleProductSearch = (searchTerm: string) => {
    debouncedSearch(searchTerm);
  };

  const handleSaveActualQuantity = (productId: string, actualQuantity: number | null, reason?: string) => {
    const updatedProducts = selectedProducts.map((product) => {
      if (product.id === productId) {
        return {
          ...product,
          actualQuantity,
          reason,
        };
      }
      return product;
    });
    setSelectedProducts(updatedProducts);
    setSelectedProduct(undefined); // Reset selected product
  };

  const handleSelectProduct = (product: ProductData) => {
    setSelectedProduct(product);
  };
  const createOrderPayload = useCallback(
    (isPublishing: boolean) => {
      return {
        code: orderId ? checkOrderData?.data?.code || '' : code || '',
        barcode: orderId ? checkOrderData?.data?.barcode || '' : generateRandomBarcode(),
        total: selectedProducts.reduce(
          (acc, product) => acc + (product.actualQuantity || 0) * (product.costPrice || 0),
          0,
        ),
        currentStep: 0,
        description: description,
        statusId: isPublishing ? completedId || '' : inprogressId || '',
        type: InventoryDocumentType.INVENTORY_CHECK_ORDER,
        inventoryDocumentItems: selectedProducts.map((item) => ({
          productVersionId: item.id,
          unitId: item.baseUnitId,
          unitConvertValue: 1,
          name: item.name,
          color: item.color,
          sizeSpec: item.sizeSpec,
          productDimension: item.dimension,
          category: item.category?.name,
          quantity: item.product?.quantity ?? item.quantity ?? 0,
          actualQuantity: item.actualQuantity ?? 0,
          description: item.reason ?? '',
          costPrice: Number(item.costPrice) ?? 0,
          total: (item.product?.quantity ?? 0) * (Number(item.costPrice) ?? 0),
        })),
      };
    },
    [code, description, selectedProducts, inprogressId, completedId],
  );
  const handleSubmit = useCallback(
    async (isPublishing: boolean) => {
      if (!selectedProducts.length) {
        showAlertNotification({
          type: 'warning',
          title: 'Danh sách rỗng',
          message: 'Vui lòng thêm ít nhất một sản phẩm để kiểm kho',
        });
        return;
      }

      const payload = createOrderPayload(isPublishing);

      try {
        let result;
        if (isPublishing) {
          if (orderId) {
            result = await updateCheckOrder({
              ...payload,
              id: orderId,
            });
          } else {
            result = await createCheckOrder(payload);
          }
        } else {
          if (orderId) {
            result = await updateCheckOrder({
              ...payload,
              id: orderId,
            });
          } else {
            result = await createCheckOrder(payload);
          }
        }

        if (result.error) {
          throw new Error('Đã xảy ra lỗi khi gọi API');
        }

        showAlertNotification({
          type: 'success',
          title: isPublishing ? 'Kiểm kho thành công' : 'Lưu đợi kiểm kho thành công',
          message: isPublishing
            ? 'Hoàn thành kiểm kho thành công'
            : 'Phiếu kiểm kho đã được lưu lại và đợi kiểm kho sau',
        });
        if (isPublishing) {
          navigate('/management/inventory/check-orders/detail/' + (result.data?.data?.id || ''));
        } else {
          navigate('/management/inventory/check-orders');
        }
      } catch (error) {
        console.error('Error creating/updating check order:', error);

        showAlertNotification({
          type: 'error',
          title: 'Kiểm kho thất bại',
          message: 'Không thể hoàn thành kiểm kho',
        });
      }
    },
    [selectedProducts, description, createOrderPayload, createCheckOrder, updateCheckOrder],
  );
  return {
    checkOrderData,
    form,
    selectedProducts,
    setSelectedProducts,
    selectedProduct,
    setSelectedProduct,
    description,
    setDescription,
    productVersionsData,
    isLoadingProducts,
    handleSelectProductToList,
    handleRemoveProduct,
    handleProductSearch,
    handleSaveActualQuantity,
    handleSelectProduct,
    handleSubmit,
    profile,
    isLoading: isLoadingCreateCheckOrder || isLoadingUpdateCheckOrder,
    loading: loading || isLoadingGenerateCode || isLoadingCheckOrderData,
  };
};
